// @file Section order helper methods
import type { Id, Section } from '@@/types'
import { sortBy } from 'es-toolkit'

export default {
  getSortIndex(timestamp?: Date): number {
    if (timestamp == null) {
      timestamp = new Date()
    }
    return new Date(timestamp.toISOString()).getTime() / 1000
  },
  getTopSortIndex(): number {
    return -this.getSortIndex()
  },
  getBottomSortIndex(): number {
    return this.getSortIndex()
  },
  sortSections(sectionsAsArray: Section[]): Section[] {
    return sortBy(sectionsAsArray, [
      (section) => {
        return section.sort_index
      },
    ])
  },
  getNewIndex(item: HTMLElement): {
    sectionId: Id
    sortIndex: number
    gapSize: number
  } | null {
    const prev = this.getValidSibling(item.previousElementSibling)
    const next = this.getValidSibling(item.nextElementSibling)

    const { sortIndex, gapSize } = this.getNewIndexBetween(prev, next)
    if (sortIndex == null || gapSize == null) {
      // Do nothing
      return null
    }
    return {
      sectionId: parseInt(item.getAttribute('data-section-id') ?? item.getAttribute('data-id') ?? ''),
      sortIndex,
      gapSize,
    }
  },
  getNewIndexBefore(item: HTMLElement): {
    sortIndex?: number
    gapSize?: number
  } {
    const prev = this.getValidSibling(item.previousElementSibling)
    return this.getNewIndexBetween(prev, item)
  },
  getNewIndexAfter(item: HTMLElement): {
    sortIndex?: number
    gapSize?: number
  } {
    const next = this.getValidSibling(item.nextElementSibling)
    return this.getNewIndexBetween(item, next)
  },
  getNewIndexBetween(
    prev: HTMLElement | null,
    next: HTMLElement | null,
  ): {
    sortIndex?: number
    gapSize?: number
  } {
    if (prev == null && next == null) {
      // Do nothing
      return {}
    }

    let prevIndex: number | null = null
    let nextIndex: number | null = null
    if (prev == null) {
      // moved to top
      prevIndex = this.getTopSortIndex()
    } else if (next == null) {
      // moved to bottom
      nextIndex = this.getBottomSortIndex()
    }
    if (prevIndex === null) {
      prevIndex = parseInt(prev?.getAttribute('data-rank') ?? '')
    }
    if (nextIndex === null) {
      nextIndex = parseInt(next?.getAttribute('data-rank') ?? '')
    }

    // New sort index is the average of the two adjacent sections' sort indices
    const sortIndex = Math.round((prevIndex + nextIndex) / 2)
    // Might be NaN, which translates to a nil in controller.
    const gapSize = Math.abs(nextIndex - prevIndex)
    return { sortIndex, gapSize }
  },
  // Helper method to validate sibling elements
  getValidSibling(element: Element | null): HTMLElement | null {
    if (element?.id == null) return null
    return element.id.match(/^section-\d+$/) != null ? (element as HTMLElement) : null
  },
}
